import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import {
  useWorkflowStatus,
  WorkflowExecutionStatus,
} from '@/services/workflows/api';
import type { WorkflowDefinition } from '@/services/workflows/api';

import type { WorkflowToast } from '.';
import { WorkflowToastContent } from './WorkflowToastContent';

export type WorkflowTrackingOptions = {
  correlationId: string;
  workflow: WorkflowDefinition;
  count: number;
  onFinished?: () => void;
};

type WorkflowStatusToastProps = WorkflowTrackingOptions & {
  notification: WorkflowToast;
};

/** Mounted by the global toaster, so polling is independent of the launching page. */
export function WorkflowStatusToast({
  correlationId,
  workflow,
  count,
  notification,
  onFinished,
}: WorkflowStatusToastProps) {
  const { t } = useTranslation();
  const { data, failureCount, isPaused } = useWorkflowStatus(correlationId);
  const finished = useRef(false);
  const label = t(`workflow.names.${workflow.name}`, {
    defaultValue: workflow.name,
  });

  useEffect(() => {
    if (!data || finished.current) {
      return;
    }

    if (data.status === WorkflowExecutionStatus.COMPLETED) {
      finished.current = true;
      notification.success(
        t('workflow.toast.completedDetail', { workflow: label, count }),
        t('workflow.toast.completedTitle', { workflow: label }),
      );
    } else if (data.status === WorkflowExecutionStatus.FAILED) {
      finished.current = true;
      const message = data.entities
        .filter((entity) => entity.status === WorkflowExecutionStatus.FAILED)
        .map((entity) => entity.errorMessage)
        .filter(Boolean)
        .join('; ');
      notification.error(
        message || t('workflow.toast.failedDetail', { workflow: label }),
        t('workflow.toast.failedTitle', { workflow: label }),
      );
    } else if (data.status === WorkflowExecutionStatus.CANCELLED) {
      finished.current = true;
      notification.cancelled();
    } else {
      return;
    }

    onFinished?.();
  }, [data, notification, label, count, onFinished, t]);

  const progress = data?.entities.some(
    (entity) => entity.completionRate !== null,
  )
    ? data.completionRate
    : null;
  const statusLabel =
    failureCount || isPaused
      ? 'workflow.toast.retrying'
      : data?.status === WorkflowExecutionStatus.PENDING
        ? 'workflow.toast.pending'
        : data?.status === WorkflowExecutionStatus.RECOVERING
          ? 'workflow.toast.recovering'
          : 'workflow.toast.processing';

  return <WorkflowToastContent progress={progress} label={t(statusLabel)} />;
}
